import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

import {
  ZfAccordion,
  ZfAccordionItem,
  ZfDialog,
  ZfDialogContent,
  ZfDialogFooter,
  ZfDialogHeader,
  ZfDivider,
  ZfIcon,
  ZfLink,
  ZfStack,
  ZfTextButton,
  ZfTypography,
} from '@zafire/react';

type ErrorDialogType = {
  showDialog: boolean;
  onDialogClose: () => void;
  error?: Error;
};
const ErrorDialog = ({ showDialog, onDialogClose, error }: ErrorDialogType) => {
  const { t } = useTranslation();

  const errorMessage = useMemo(() => {
    if (error) {
      const result = Object.entries(error).map(([key, value]) => ({
        title: key,
        label: Array.isArray(value) || typeof value === 'object' ? JSON.stringify(value) : value,
      }));
      return result;
    }
    return null;
  }, [error]);
  return (
    <ZfDialog show={showDialog} size="medium" onDialogClose={onDialogClose} disableClose>
      <ZfDialogHeader></ZfDialogHeader>
      <ZfDialogContent>
        <ZfStack spacing="medium">
          <ZfStack direction="row" alignment="center" justify="center">
            <ZfStack alignment="center">
              <ZfIcon name="warning" size="extra-large" style={{ color: 'red', '--zf-icon-size-xxl': 48 } as any} />
              <ZfTypography variant="display-medium" color="emphasis" weight="bold">
                {t('contactNote.form.errorDialog.title')}
              </ZfTypography>
            </ZfStack>
          </ZfStack>
          <ZfTypography variant="body-medium" color="normal">
            {t('contactNote.form.errorDialog.titleDetails')}
          </ZfTypography>
          <ZfDivider />
          <ZfAccordion>
            <ZfAccordionItem showUnderline={true} titleText={t('contactNote.form.errorDialog.whatToTry')}>
              <ZfStack direction="row">
                <ZfTypography variant="body-medium" color="normal">
                  {t('contactNote.form.errorDialog.whatToTryDetailsOne')}
                </ZfTypography>
                <ZfLink size="medium" type="inline" target="_blank" rel="noreferrer" trailingIcon="open_in_new" href="">
                  {t('contactNote.form.errorDialog.createSupportTicket')}
                </ZfLink>
              </ZfStack>

              <ZfTypography variant="body-medium" color="normal">
                {t('contactNote.form.errorDialog.whatToTryDetailTwo')}
              </ZfTypography>
            </ZfAccordionItem>
          </ZfAccordion>
          <ZfAccordion>
            <ZfAccordionItem
              titleText={t('contactNote.form.errorDialog.technicalDetails')}
              description={t('contactNote.form.errorDialog.expandDetails')}
            >
              <ZfStack>
                {errorMessage?.map((item) => (
                  <ZfStack direction="row" key={item.title}>
                    <ZfTypography style={{ flex: 1 }}>{item.title}</ZfTypography>
                    <ZfTypography style={{ flex: 1 }}>{item.label}</ZfTypography>
                  </ZfStack>
                ))}
              </ZfStack>
            </ZfAccordionItem>
          </ZfAccordion>
        </ZfStack>
      </ZfDialogContent>
      <ZfDialogFooter>
        <ZfStack slot="primary" direction="row" spacing="small">
          <ZfTextButton hierarchy="tertiary" size="medium" onClick={() => onDialogClose()}>
            {t('contactNote.form.errorDialog.close')}
          </ZfTextButton>
          <ZfTextButton
            leadingIcon="content_copy"
            hierarchy="secondary"
            size="medium"
            onClick={() => navigator.clipboard.writeText(JSON.stringify(error))}
          >
            {t('contactNote.form.errorDialog.copy')}
          </ZfTextButton>
        </ZfStack>
      </ZfDialogFooter>
    </ZfDialog>
  );
};

export default ErrorDialog;
